import { useState, useRef, useEffect, useMemo } from "react";
import sendIcon from '#/assets/send.svg';
import { useTranslation } from 'react-i18next';
import { useScreenSize, ScreenSize } from '#/hooks/useScreenSize';
import AudioRecord from '../audio-record/audio-record';
import { useRecording } from "#/contexts/RecordingContext";
import { AudioPlaybackManager } from '../audio-playback/audio-playback-manager';
import { RecordingMode } from '#/types/preferences';
import styles from './prompt.module.css';
import { useBranding } from '#/branding/BrandingContext';

interface PromptProps {
  onChange: (promptTextAreaRef: React.RefObject<HTMLTextAreaElement | null>) => void;
  onSubmit: () => void;
}

type PromptContainerProps = {
  onSubmit: (prompt: string) => void;
  audioConfidence: number;
  isMuted: boolean;
  audioOutputFileName: string;
}

export function Prompt({ onChange, onSubmit }: PromptProps) {
  const { t } = useTranslation();
  const { promptPlaceholder } = useBranding();
  const [height, setHeight] = useState<number>(1);
  const [width, setWidth] = useState<number>(70);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const handleResize = () => {
      const marginOffset = getComputedStyle(document.documentElement)
        .getPropertyValue('--margin-offset')
        .trim();

      if (window.innerWidth < 330) {
        setWidth(22);
      } else if (window.innerWidth > 1125) {
        setWidth(75);
      } else {
        const effectiveWidth = window.innerWidth - (marginOffset ? parseInt(marginOffset) : 0);
        setWidth(effectiveWidth / 14.0);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  function handleChange(event: React.ChangeEvent<HTMLTextAreaElement | null> | React.ClipboardEvent<HTMLTextAreaElement>) {
    const target = event.target as HTMLTextAreaElement;

    setHeight(Math.ceil(Math.max(1, target.value.length / width)));
    onChange(textAreaRef);
  }

  function handleEnter(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    const value = textAreaRef?.current?.value;
    if (event.key === 'Enter' && value && value.trim().length > 0) {
      event.preventDefault();
      onSubmit();
    }
  }

  return (
    <textarea
      id='promptTextArea'
      placeholder={promptPlaceholder ?? t("askSpogen")}
      cols={width}
      rows={height}
      onChange={handleChange}
      onPaste={handleChange}
      onKeyDown={handleEnter}
      ref={textAreaRef}
    />
  );
}

export default function PromptContainer({
  onSubmit,
  audioConfidence,
  isMuted,
  audioOutputFileName,
}: PromptContainerProps) {
  const { recordingMode } = useRecording();
  const isAutomatic = recordingMode === RecordingMode.Automatic;
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState<boolean>(true);
  const [iconClassName, setIconClassName] = useState<string>(styles.iconDisabled);
  const [hasText, setHasText] = useState(false);
  const screenSize = useScreenSize();
  const { poweredByLogo } = useBranding();

  useEffect(() => {
    if (isAutomatic) {
      // Going to automatic: clear any typed text and force mic UI
      if (textAreaRef.current) {
        textAreaRef.current.value = '';
      }
      setHasText(false);
      setIsSubmitButtonDisabled(true);
      setIconClassName(styles.iconDisabled);
    } else {
      // Going back to manual: wait for <Prompt> to mount, then sync button state
      requestAnimationFrame(() => {
        updateSubmitButtonState();
      });
    }
  }, [isAutomatic]);

  const updateSubmitButtonState = () => {
    const hasValue = Boolean(textAreaRef.current && textAreaRef.current.value.length > 0);
    setIsSubmitButtonDisabled(!hasValue);
    setIconClassName(!hasValue ? styles.iconDisabled : styles.icon);
    setHasText(hasValue);
  }

  const handleSubmit = () => {
    if (textAreaRef.current) {
      const promptText: string = textAreaRef.current.value;
      textAreaRef.current.value = "";
      if (screenSize === ScreenSize.MOBILE) {
        textAreaRef.current.blur();
      } else {
        textAreaRef.current.rows = 1;
      }
      updateSubmitButtonState();
      onSubmit(promptText);
    }
  }

  const controls = useMemo(() => ({
    controls: (
      <div className={styles.controls}>
        <AudioPlaybackManager
          audioOutputFileName={audioOutputFileName}
          isMuted={isMuted}
        />
        <div style={{ display: hasText ? 'none' : 'block' }}>
          <AudioRecord
            audioConfidence={audioConfidence}
          />
        </div>
        <div style={{ display: hasText ? 'block' : 'none' }}>
          <button
            onClick={handleSubmit}
            disabled={isSubmitButtonDisabled}
            style={{ display: hasText ? 'block' : 'none' }}
          >
            <img src={sendIcon} id="submitIcon" className={iconClassName} alt="Send" />
          </button>
        </div>
      </div>
    )
  }), [
    isSubmitButtonDisabled,
    iconClassName,
    audioOutputFileName,
    audioConfidence,
    recordingMode,
    hasText
  ]);


  useEffect(() => {
    updateSubmitButtonState();
  }, []);

  const receivePromptFromChild = (ref: React.RefObject<HTMLTextAreaElement | null>) => {
    textAreaRef.current = ref.current;
    updateSubmitButtonState();
  }

  return (
    <div className={styles.promptContainer}>
      <div className={styles.innerPromptContainer}>
        {!isAutomatic && (
          <Prompt onChange={receivePromptFromChild} onSubmit={handleSubmit} />
        )}

        {/* Right-edge logo — automatic mode only */}
        {isAutomatic && window.innerWidth > 550 && (
          <div className={styles.logoContainer}>
            <span className={styles.logoText}>Powered by</span>
            <img src={poweredByLogo} className={styles.logoImg} alt="Spogen logo" />
          </div>
        )}

        <div className={styles.submitDiv}>
          {controls.controls}
        </div>
      </div>
    </div>
  );
}